import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { TransformControls } from 'three/addons/controls/TransformControls.js';
import { CSS2DObject, CSS2DRenderer } from 'three/addons/renderers/CSS2DRenderer.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { EDGE_IDS, partBox, PIN_POSITIONS, toMetres, buildRoom, wallTopAt, type RoomPlan, type RoomItem, type RoomWallBuild, type EdgeId, type EvaluatedProject, type PanelPart, type Pin, type ProjectDocument, type Vec3, formatMm } from '@scs/domain-engine';
import { evalMm } from '../lib/mmexpr';
import type { DecorView } from '../lib/catalog';

/**
 * Renderer adapter. Consumes the evaluated domain model (µm) and converts to metres at this boundary only.
 * Nothing in the project model depends on Three.js objects; the scene is rebuilt from data.
 */
interface RoomItemEntry {
  box: THREE.Mesh;
  w: RoomWallBuild;
  item: RoomItem;
  /** Start along the wall (µm from the wall start) and height above the floor, live while dragging. */
  u0: number;
  elev: number;
  /** Shown height (cut to the wall top) and offset through the wall of the box centre. */
  h: number;
  v: number;
  parent: THREE.Object3D;
  dims: THREE.Group | null;
}

export interface ViewOptions {
  grid: boolean;
  xray: boolean;
  exploded: boolean;
  labels: boolean;
  edges: boolean;
  openFronts: boolean;
  /** Dashed lines between linked dimensions. */
  links: boolean;
  /** Walls, floor and installations of the room ("Prostorija"). */
  room: boolean;
}

/** What the move gizmo / point-snap tool moves. */
export type MoveTarget = { kind: 'unit'; unitId: string } | { kind: 'panel'; unitId: string; partId: string };

export interface LinkLine {
  from: Vec3; // µm, world
  to: Vec3;
  mode: 'equal' | 'offset';
}

const M = 1_000_000; // µm per metre

/** Small hinge cups on the hinged edge of a front (2–4 by edge length), drawn on its back face. */
function hingeMarkers(side: 'left' | 'right' | 'top' | 'bottom', sx: number, sy: number, sz: number): THREE.Group {
  const g = new THREE.Group();
  const vertical = side === 'left' || side === 'right';
  const edge = vertical ? sy : sx;
  const n = edge <= 0.9 ? 2 : edge <= 1.6 ? 3 : 4;
  const mat = new THREE.MeshBasicMaterial({ color: 0x8a9099 });
  for (let i = 0; i < n; i++) {
    const t = -edge / 2 + 0.1 + ((edge - 0.2) * i) / (n - 1);
    const cup = new THREE.Mesh(new THREE.CylinderGeometry(0.0175, 0.0175, 0.004, 20), mat);
    cup.rotation.x = Math.PI / 2;
    const inset = 0.0215;
    if (vertical) cup.position.set(side === 'left' ? -sx / 2 + inset : sx / 2 - inset, t, -sz / 2 - 0.002);
    else cup.position.set(t, side === 'bottom' ? -sy / 2 + inset : sy / 2 - inset, -sz / 2 - 0.002);
    g.add(cup);
  }
  g.name = 'hinges';
  return g;
}
const targetKey = (t: MoveTarget) => (t.kind === 'unit' ? `u:${t.unitId}` : `p:${t.partId}`);

/** A box edge of a part: runs along `axis` (part-local), on the ± sides of the two other axes. */
export interface EdgeRef {
  partId: string;
  axis: 0 | 1 | 2;
  side: [-1 | 1, -1 | 1];
}
const sameEdge = (a: EdgeRef, b: EdgeRef) => a.partId === b.partId && a.axis === b.axis && a.side[0] === b.side[0] && a.side[1] === b.side[1];

/** Flip orientation icon: a panel in perspective with a 90° turn arrow around it. */
const FLIP_ICON = `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
  <path d="M6.5 5.5 L14.5 3.5 L14.5 14 L6.5 16 Z" fill="currentColor" fill-opacity="0.22"/>
  <path d="M17.6 7.2 A8 8 0 0 1 10.2 20.6"/>
  <path d="M10.2 20.6 L13.3 21.3"/>
  <path d="M10.2 20.6 L11.3 17.7"/>
</svg>`;

export type ViewName = 'persp' | 'front' | 'back' | 'left' | 'right' | 'top' | 'bottom';

export interface PickResult {
  cabinetId: string;
  partId: string;
}

/** Selection: the whole unit green, the selected component (panel, shelf, drawer, front) lime on top. */
const SEL_UNIT = 0x34d27b;
const SEL_UNIT_DIM = 0x23915a;
const SEL_PART = 0xc8ff3c;
const SEL_PART_FILL = new THREE.MeshBasicMaterial({ color: SEL_PART, transparent: true, opacity: 0.22, depthWrite: false });
/** Room ("Prostorija") materials, shared (never disposed with the scene). */
const ROOM_MAT = {
  wall: new THREE.MeshStandardMaterial({ color: 0xd8d3ca, roughness: 0.95 }),
  wallHover: new THREE.MeshStandardMaterial({ color: 0x34d27b, roughness: 0.9 }),
  /** Selected wall (green tint) and the same wall when it is cut away (ghost, not pickable). */
  wallSel: new THREE.MeshStandardMaterial({ color: 0x9fe3bb, roughness: 0.9 }),
  wallGhost: new THREE.MeshStandardMaterial({ color: 0x34d27b, transparent: true, opacity: 0.25, depthWrite: false }),
  floor: new THREE.MeshStandardMaterial({ color: 0x8c8172, roughness: 1, side: THREE.DoubleSide }),
  glass: new THREE.MeshStandardMaterial({ color: 0x8fc3ea, transparent: true, opacity: 0.35, roughness: 0.1 }),
  door: new THREE.MeshStandardMaterial({ color: 0x8a6440, roughness: 0.7 }),
  socket: new THREE.MeshStandardMaterial({ color: 0xf4f4f4, roughness: 0.4 }),
  drain: new THREE.MeshStandardMaterial({ color: 0x7a7f86, roughness: 0.5 }),
  water: new THREE.MeshStandardMaterial({ color: 0x2f80ed, roughness: 0.4 }),
};
const LEG_MAT = new THREE.MeshStandardMaterial({ color: 0x2b2f34, roughness: 0.5, metalness: 0.2 });
/** Dimension lines of room items (distance from the wall end and from the floor). */
const DIM_MAT = new THREE.LineBasicMaterial({ color: 0xffc94d, depthTest: false, transparent: true });
const EDGE_COLORS = { band: 0xe8813a, none: 0x4f5964, unresolved: 0xe04f9a, factory: 0x5fbf8f, seal: 0x6aa8e8 } as const;

export class ThreeViewport {
  private renderer: THREE.WebGLRenderer;
  private labelRenderer: CSS2DRenderer;
  private scene = new THREE.Scene();
  private persp: THREE.PerspectiveCamera;
  private ortho: THREE.OrthographicCamera;
  private camera: THREE.Camera;
  private controls: OrbitControls;
  private content = new THREE.Group();
  private grid: THREE.GridHelper;
  private floor: THREE.Mesh;
  private materials = new Map<string, THREE.MeshStandardMaterial>();
  private textures = new Map<string, THREE.Texture>();
  private decors = new Map<string, DecorView>();
  private loader = new THREE.TextureLoader();
  private meshes: THREE.Mesh[] = [];
  private raf = 0;
  private ro: ResizeObserver;
  private down: { x: number; y: number } | null = null;
  private selection: { cabinetIds: Set<string>; partId: string | null; extra: Set<string> } = { cabinetIds: new Set(), partId: null, extra: new Set() };
  private lastBounds = new THREE.Box3();
  /** Camera glide to a problem spot (issue click). */
  private glide: { t0: number; fromT: THREE.Vector3; toT: THREE.Vector3; fromP: THREE.Vector3; toP: THREE.Vector3 } | null = null;
  /** Red box around the spot of the focused issue. */
  private issueMarker: THREE.Box3Helper | null = null;
  onPick: (hit: PickResult | null, mod: { shift: boolean; ctrl: boolean }) => void = () => {};
  /** Click on a room wall or on a window / door / socket … in it. */
  onRoomPick: (hit: { lineId: string; itemId: string | null }) => void = () => {};
  /** An item was dragged along its wall: new start along the wall (µm from the wall start) and height above the floor. */
  onRoomItemMove: (itemId: string, u0: number, elevation: number) => void = () => {};
  /** Live snap during gizmo drag: raw world delta (µm) → corrected delta + description. */
  onSnap: (target: MoveTarget, raw: Vec3) => { delta: Vec3; info: string } = (_t, raw) => ({ delta: raw.map(Math.round) as Vec3, info: '' });
  /** Committed move (world delta, µm). `duplicate` = Alt was held: copy instead of moving. */
  onMove: (target: MoveTarget, delta: Vec3, opts?: { duplicate: boolean }) => void = () => {};
  /** Alt-drag state for the overlay hint. */
  onDuplicateDrag: (active: boolean) => void = () => {};
  /** Exact move typed after a gizmo drag: replaces the last drag's world delta. */
  onExactMove: (target: MoveTarget, from: Vec3, to: Vec3) => void = () => {};
  private moveBox!: HTMLDivElement;
  private moveInfo: { key: string; target: MoveTarget; delta: Vec3; live: boolean } | null = null;

  /** Flip orientation button (90° about an axis). */
  onFlip: (target: MoveTarget, axis: 'x' | 'y' | 'z') => void = () => {};

  private altHeld = false;
  private shiftHeld = false;
  private dragDuplicate = false;
  private ghost: THREE.Object3D | null = null;
  private flipBar!: HTMLDivElement;
  private flipAxes: ('x' | 'y' | 'z')[] = [];
  /** Corner snap state for the overlay hint. */
  onCornerState: (state: 'idle' | 'selected') => void = () => {};
  /** "Izaberi prostor": a compartment box was clicked. */
  onRegionPick: (index: number, additive: boolean) => void = () => {};
  /** Whether a region may be picked (false → shown red, the click does nothing). */
  regionGuard: (index: number, additive: boolean) => boolean = () => true;
  private regionSelected = new Set<number>();
  private hoverBlocked = false;
  private ctrlHeld = false;
  private regions: { unitId: string; boxes: { min: Vec3; max: Vec3 }[] } | null = null;
  private regionMeshes: THREE.Mesh[] = [];
  private hoverRegion = -1;

  /** Edge pick: a source edge on the selected panel, then a target edge on any panel. */
  onEdgePick: (state: { source: EdgeRef | null; target: EdgeRef | null }) => void = () => {};

  private hoverEdge: EdgeRef | null = null;
  private edgeSource: EdgeRef | null = null;
  private edgeTarget: EdgeRef | null = null;
  private edgeBars!: { hover: THREE.Mesh; source: THREE.Mesh; target: THREE.Mesh };
  /** Pin (anchor point) moved to a new snap position. */
  onPinChange: (target: MoveTarget, pin: Pin) => void = () => {};
  onPinState: (state: 'idle' | 'moving') => void = () => {};

  private pin: { target: MoveTarget; value: Pin; unitSize: Vec3 | null } | null = null;
  private pinMarker!: THREE.Mesh;
  private pinPreview!: THREE.Mesh;
  private pinButton!: HTMLButtonElement;
  private pinMoving = false;
  private pinCandidate: Pin | null = null;

  private tc: TransformControls;
  private proxy = new THREE.Object3D();
  private gizmoTarget: MoveTarget | null = null;
  private dragStart: { proxy: THREE.Vector3; obj: THREE.Object3D; objPos: THREE.Vector3; toLocal: (d: THREE.Vector3) => THREE.Vector3 } | null = null;
  private dragDelta: Vec3 = [0, 0, 0];
  private suppressPick = false;
  private unitGroups = new Map<string, THREE.Group>();
  private partHolders = new Map<string, THREE.Group>();
  private linkGroup = new THREE.Group();
  /** What a corner snap moves (independent of whether the gizmo is shown). */
  private moveTarget: MoveTarget | null = null;
  /** Corner chosen on the selection; next click on another panel's corner snaps it there. */
  private cornerSource: THREE.Vector3 | null = null;
  private hoverCorner: THREE.Vector3 | null = null;
  private hoverMarker: THREE.Mesh;
  private sourceMarker: THREE.Mesh;
  private cornersEnabled = true;

  constructor(private readonly host: HTMLElement) {
    this.renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
    if (!this.renderer.capabilities.isWebGL2) console.warn('WebGL2 nije dostupan; prikaz može biti ograničen.');
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.AgXToneMapping;
    this.renderer.toneMappingExposure = 1.0;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    host.appendChild(this.renderer.domElement);

    this.labelRenderer = new CSS2DRenderer();
    this.labelRenderer.domElement.style.position = 'absolute';
    this.labelRenderer.domElement.style.inset = '0';
    this.labelRenderer.domElement.style.pointerEvents = 'none';
    host.appendChild(this.labelRenderer.domElement);

    const pmrem = new THREE.PMREMGenerator(this.renderer);
    this.scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    this.scene.background = new THREE.Color(0x101316);

    this.persp = new THREE.PerspectiveCamera(40, 1, 0.01, 200);
    this.persp.position.set(2.6, 1.8, 3.4);
    this.ortho = new THREE.OrthographicCamera(-2, 2, 2, -2, -100, 100);
    this.camera = this.persp;
    this.controls = new OrbitControls(this.persp, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.target.set(0.6, 0.4, 0.3);

    const hemi = new THREE.HemisphereLight(0xffffff, 0x404040, 0.6);
    const sun = new THREE.DirectionalLight(0xffffff, 1.6);
    sun.position.set(3, 5, 4);
    sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    sun.shadow.camera.left = -5;
    sun.shadow.camera.right = 5;
    sun.shadow.camera.top = 5;
    sun.shadow.camera.bottom = -5;
    sun.shadow.bias = -0.0004;
    sun.shadow.normalBias = 0.01;
    this.scene.add(hemi, sun, this.content);

    this.grid = new THREE.GridHelper(10, 100, 0x3a424c, 0x252b32);
    (this.grid.material as THREE.Material).transparent = true;
    (this.grid.material as THREE.Material).opacity = 0.6;
    this.floor = new THREE.Mesh(new THREE.PlaneGeometry(20, 20), new THREE.ShadowMaterial({ opacity: 0.25 }));
    this.floor.rotation.x = -Math.PI / 2;
    this.floor.receiveShadow = true;
    this.scene.add(this.grid, this.floor, this.linkGroup, this.proxy);

    this.tc = new TransformControls(this.persp, this.renderer.domElement);
    this.tc.setMode('translate');
    this.tc.setSize(0.8);
    this.scene.add(this.tc.getHelper());
    this.tc.addEventListener('dragging-changed', (e) => {
      const dragging = (e as unknown as { value: boolean }).value;
      this.controls.enabled = !dragging;
      if (dragging) this.beginDrag();
      else this.endDrag();
    });
    this.tc.addEventListener('objectChange', () => this.duringDrag());

    const marker = (color: number) => {
      const m = new THREE.Mesh(new THREE.SphereGeometry(1, 16, 12), new THREE.MeshBasicMaterial({ color, depthTest: false, transparent: true, opacity: 0.95 }));
      m.renderOrder = 12;
      m.visible = false;
      // Constant on-screen size (~7 px radius) regardless of zoom.
      m.onBeforeRender = (_r, _s, cam) => {
        const px = cam instanceof THREE.OrthographicCamera ? (cam.top - cam.bottom) / Math.max(1, this.host.clientHeight) : (2 * Math.tan((((cam as THREE.PerspectiveCamera).fov ?? 40) * Math.PI) / 360) * cam.position.distanceTo(m.position)) / Math.max(1, this.host.clientHeight);
        m.scale.setScalar(px * 7);
        m.updateMatrixWorld();
      };
      this.scene.add(m);
      return m;
    };
    this.hoverMarker = marker(0x2f80ed);
    this.sourceMarker = marker(0xd6a467);

    // Edge highlight: a bar along the edge, ~4 px thick on screen.
    const bar = (color: number) => {
      const m = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1), new THREE.MeshBasicMaterial({ color, depthTest: false, transparent: true, opacity: 0.95 }));
      m.renderOrder = 12;
      m.visible = false;
      m.onBeforeRender = (_r, _s, cam) => {
        const px = cam instanceof THREE.OrthographicCamera ? (cam.top - cam.bottom) / Math.max(1, this.host.clientHeight) : (2 * Math.tan((((cam as THREE.PerspectiveCamera).fov ?? 40) * Math.PI) / 360) * cam.position.distanceTo(m.position)) / Math.max(1, this.host.clientHeight);
        m.scale.y = m.scale.z = px * 4;
        m.updateMatrixWorld();
      };
      this.scene.add(m);
      return m;
    };
    this.edgeBars = { hover: bar(0x2f80ed), source: bar(0xd6a467), target: bar(0x4fd66a) };

    // Pin: magenta diamond, constant screen size; the preview follows the mouse while moving it.
    const diamond = (color: number, opacity: number) => {
      const m = new THREE.Mesh(new THREE.OctahedronGeometry(1), new THREE.MeshBasicMaterial({ color, depthTest: false, transparent: true, opacity }));
      m.renderOrder = 13;
      m.visible = false;
      m.onBeforeRender = (_r, _s, cam) => {
        const px = cam instanceof THREE.OrthographicCamera ? (cam.top - cam.bottom) / Math.max(1, this.host.clientHeight) : (2 * Math.tan((((cam as THREE.PerspectiveCamera).fov ?? 40) * Math.PI) / 360) * cam.position.distanceTo(m.position)) / Math.max(1, this.host.clientHeight);
        m.scale.setScalar(px * 9);
        m.updateMatrixWorld(); // applies this frame (modelViewMatrix is computed after onBeforeRender)
      };
      this.scene.add(m);
      return m;
    };
    this.pinMarker = diamond(0xff4fd8, 1);
    this.pinPreview = diamond(0xff4fd8, 0.55);
    this.pinButton = document.createElement('button');
    this.pinButton.className = 'pin-btn';
    this.pinButton.type = 'button';
    this.pinButton.textContent = '✥ Pomjeri pin';
    this.pinButton.style.display = 'none';
    this.pinButton.addEventListener('pointerdown', (e) => e.stopPropagation());
    this.pinButton.addEventListener('click', (e) => {
      e.stopPropagation();
      this.startPinMove();
    });
    host.appendChild(this.pinButton);

    // Alt held while dragging the gizmo = duplicate (like Maya/Blender). Prevent the browser menu on Alt.
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);

    // Move readout next to the gizmo: live mm while dragging, editable after release.
    this.moveBox = document.createElement('div');
    this.moveBox.className = 'move-box';
    this.moveBox.style.display = 'none';
    this.moveBox.addEventListener('pointerdown', (e) => e.stopPropagation());
    host.appendChild(this.moveBox);

    this.flipBar = document.createElement('div');
    this.flipBar.className = 'flip-bar';
    this.flipBar.style.display = 'none';
    this.flipBar.addEventListener('pointerdown', (e) => e.stopPropagation());
    host.appendChild(this.flipBar);

    this.ro = new ResizeObserver(() => this.resize());
    this.ro.observe(host);
    this.resize();

    const el = this.renderer.domElement;
    // Capture phase: runs before the orbit controls, so a drag on a room item does not turn the view.
    el.addEventListener('pointerdown', this.onItemDown, true);
    el.addEventListener('pointerdown', this.onDown);
    el.addEventListener('pointerup', this.onUp);
    el.addEventListener('pointermove', this.onHover);
    el.addEventListener('contextmenu', this.onContextMenu);
    const loop = () => {
      this.raf = requestAnimationFrame(loop);
      this.stepGlide();
      this.cutawayWalls();
      this.controls.update();
      this.positionFlipBar();
      this.positionMoveBox();
      this.renderer.render(this.scene, this.camera);
      this.labelRenderer.render(this.scene, this.camera);
    };
    loop();
  }

  dispose() {
    cancelAnimationFrame(this.raf);
    this.ro.disconnect();
    this.controls.dispose();
    this.tc.dispose();
    this.clearContent();
    this.materials.forEach((m) => m.dispose());
    this.textures.forEach((t) => t.dispose());
    this.renderer.dispose();
    this.renderer.domElement.remove();
    this.labelRenderer.domElement.remove();
    this.pinButton.remove();
    this.flipBar.remove();
    this.moveBox.remove();
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
  }

  private resize() {
    const w = Math.max(1, this.host.clientWidth);
    const h = Math.max(1, this.host.clientHeight);
    this.renderer.setSize(w, h);
    this.labelRenderer.setSize(w, h);
    this.persp.aspect = w / h;
    this.persp.updateProjectionMatrix();
    this.updateOrthoFrustum();
  }

  private updateOrthoFrustum() {
    const w = Math.max(1, this.host.clientWidth);
    const h = Math.max(1, this.host.clientHeight);
    const size = this.ortho.userData.size ?? 3;
    this.ortho.left = (-size * w) / h / 2;
    this.ortho.right = (size * w) / h / 2;
    this.ortho.top = size / 2;
    this.ortho.bottom = -size / 2;
    this.ortho.updateProjectionMatrix();
  }

  private onDown = (e: PointerEvent) => {
    this.down = { x: e.clientX, y: e.clientY };
    if (e.button === 2) this.rightDown = { x: e.clientX, y: e.clientY };
  };
  private rightDown: { x: number; y: number } | null = null;
  /** Gizmo handle of the current drag ('X', 'XY', 'XYZ' = centre …). */
  private dragAxis: string | null = null;

  private ray(e: PointerEvent): THREE.Raycaster {
    const rect = this.renderer.domElement.getBoundingClientRect();
    const ndc = new THREE.Vector2(((e.clientX - rect.left) / rect.width) * 2 - 1, -((e.clientY - rect.top) / rect.height) * 2 + 1);
    const ray = new THREE.Raycaster();
    ray.setFromCamera(ndc, this.camera);
    // Point picking tolerance ≈ 8 px at the current zoom.
    const dist = this.camera.position.distanceTo(this.controls.target);
    ray.params.Points = { threshold: Math.max(0.004, dist * 0.012) };
    return ray;
  }

  private onUp = (e: PointerEvent) => {
    if (!this.down) return;
    const moved = Math.hypot(e.clientX - this.down.x, e.clientY - this.down.y);
    this.down = null;
    if (this.suppressPick) {
      this.suppressPick = false;
      return;
    }
    if (moved > 4 || e.button !== 0) return;
    if (this.wallPick) {
      const id = this.wallUnder(e);
      if (id) this.wallPick(id);
      return;
    }
    if (this.regions) {
      const additive = e.ctrlKey || e.metaKey;
      if (this.hoverRegion >= 0 && this.regionGuard(this.hoverRegion, additive)) this.onRegionPick(this.hoverRegion, additive);
      return;
    }
    if (this.pinMoving) {
      if (this.pinCandidate && this.pin) {
        const target = this.pin.target;
        const value = this.pinCandidate;
        this.endPinMove();
        this.onPinChange(target, value);
      }
      return;
    }
    // Ctrl/Shift+click always (de)selects panels, even over a corner or an edge.
    const multi = e.shiftKey || e.ctrlKey || e.metaKey;
    if (!multi && (this.edgeSource || (this.hoverEdge && !this.hoverCorner)) && this.edgeClick()) return;
    if (!multi && this.cornerClick()) return;
    const pickRoom = this.roomPickables();
    const hit = this.ray(e).intersectObjects([...this.meshes, ...pickRoom], false)[0];
    const room = hit?.object.userData as { lineId?: string; itemId?: string } | undefined;
    if (room?.lineId && !multi) return this.onRoomPick({ lineId: room.lineId, itemId: room.itemId ?? null });
    const data = (room?.lineId ? undefined : hit?.object.userData) as PickResult | undefined;
    this.onPick(data?.partId ? { cabinetId: data.cabinetId, partId: data.partId } : null, { shift: e.shiftKey, ctrl: e.ctrlKey || e.metaKey });
  };

  private onHover = (e: PointerEvent) => {
    if (this.dragStart || this.tc.dragging) return;
    if (this.pinMoving) return this.pinMoveHover(e);
    if (this.regions) return this.regionHover(e);
    if (this.wallPick) {
      const id = this.wallUnder(e);
      if (id !== this.hoverWall) this.setHoverWall(id);
      return;
    }
    this.pinButtonHover(e);
    this.hoverCorner = this.cornersEnabled && this.moveTarget && !this.edgeSource ? this.nearestCorner(e) : null;
    this.hoverMarker.visible = !!this.hoverCorner;
    if (this.hoverCorner) this.hoverMarker.position.copy(this.hoverCorner);
    this.hoverEdge = this.cornersEnabled && this.moveTarget && !this.hoverCorner && !this.cornerSource ? this.nearestEdge(e) : null;
    this.placeEdgeBar(this.edgeBars.hover, this.hoverEdge);
    this.renderer.domElement.style.cursor = this.hoverCorner || this.hoverEdge ? 'crosshair' : '';
  };

  // ---------- move gizmo ----------

  /** Shows the gizmo on a unit (origin corner) or a panel (its min corner). `null` hides it. */
  setGizmo(target: MoveTarget | null) {
    this.gizmoTarget = target;
    if (this.moveInfo && (!target || targetKey(target) !== this.moveInfo.key) && !this.dragStart) this.setMoveInfo(null);
    if (this.dragStart || this.roomGizmoDrag) return; // never re-anchor during a drag
    this.roomGizmo = null;
    this.proxy.rotation.set(0, 0, 0);
    this.tc.space = 'world';
    this.tc.showZ = true;
    const obj = target ? (target.kind === 'unit' ? this.unitGroups.get(target.unitId) : this.partHolders.get(target.partId)) : undefined;
    if (!target || !obj) {
      this.tc.detach();
      return;
    }
    const box = new THREE.Box3().setFromObject(obj);
    // A unit: at its origin (which can be put on any corner, see the corner menu) when the origin is
    // on the unit; otherwise (an origin far away) at the corner of its box.
    const origin = obj.getWorldPosition(new THREE.Vector3());
    const onUnit = box.clone().expandByScalar(0.001).containsPoint(origin);
    this.proxy.position.copy(target.kind === 'unit' ? (onUnit ? origin : new THREE.Vector3(box.min.x, box.min.y, box.min.z)) : box.getCenter(new THREE.Vector3()));
    this.tc.attach(this.proxy);
  }

  private onKeyDown = (e: KeyboardEvent) => {
    if ((e.key === 'Control' || e.key === 'Meta') && this.regions) {
      this.ctrlHeld = true;
      this.paintRegions();
    }
    if (e.key === 'Shift') this.shiftHeld = true;
    if (e.key === 'Alt') {
      this.altHeld = true;
      e.preventDefault();
    }
  };
  private onKeyUp = (e: KeyboardEvent) => {
    if ((e.key === 'Control' || e.key === 'Meta') && this.regions) {
      this.ctrlHeld = false;
      this.paintRegions();
    }
    if (e.key === 'Shift') this.shiftHeld = false;
    if (e.key === 'Alt') {
      this.altHeld = false;
      e.preventDefault();
    }
  };
  private onBlur = () => {
    this.altHeld = false;
    this.shiftHeld = false;
  };

  private beginDrag() {
    if (this.roomGizmo) {
      const r = this.roomGizmo;
      this.roomGizmoDrag = { p0: this.proxy.position.clone(), u0: r.u0, elev: r.elev, moved: false };
      return;
    }
    const t = this.gizmoTarget;
    if (!t) return;
    const obj = t.kind === 'unit' ? this.unitGroups.get(t.unitId) : this.partHolders.get(t.partId);
    if (!obj) return;
    this.dragDuplicate = this.altHeld;
    if (this.dragDuplicate) {
      // The original stays visible where it is; the dragged object represents the copy.
      this.ghost = obj.clone();
      obj.parent!.add(this.ghost);
      this.onDuplicateDrag(true);
    }
    // World → parent-local conversion for live preview of a panel inside a rotated/mirrored unit.
    const parent = obj.parent!;
    const toLocal = (d: THREE.Vector3) => {
      const inv = new THREE.Matrix4().copy(parent.matrixWorld).invert();
      const o = new THREE.Vector3().applyMatrix4(inv);
      return d.clone().applyMatrix4(inv).sub(o);
    };
    this.dragStart = { proxy: this.proxy.position.clone(), obj, objPos: obj.position.clone(), toLocal };
    this.dragDelta = [0, 0, 0];
    this.dragAxis = this.tc.axis;
  }

  private duringDrag() {
    const rd = this.roomGizmoDrag;
    if (rd && this.roomGizmo) {
      const r = this.roomGizmo;
      const d = this.proxy.position.clone().sub(rd.p0);
      // Only the components in the wall plane count: along the wall and up.
      const du = (d.x * r.w.ux + d.z * r.w.uz) * 1e6;
      const dy = d.y * 1e6;
      this.moveItemTo(r, rd.u0 + du, rd.elev + dy, this.shiftHeld ? 1000 : 10_000);
      rd.moved = r.u0 !== rd.u0 || r.elev !== rd.elev;
      this.proxy.position.copy(r.box.position);
      return;
    }
    const st = this.dragStart;
    const t = this.gizmoTarget;
    if (!st || !t) return;
    const raw = this.proxy.position.clone().sub(st.proxy);
    const r = this.onSnap(t, [Math.round(raw.x * M), Math.round(raw.y * M), Math.round(raw.z * M)]);
    this.dragDelta = r.delta;
    this.setMoveInfo({ key: targetKey(t), target: t, delta: r.delta, live: true });
    const d = new THREE.Vector3(r.delta[0] / M, r.delta[1] / M, r.delta[2] / M);
    this.proxy.position.copy(st.proxy).add(d);
    st.obj.position.copy(st.objPos).add(st.toLocal(d));
  }

  private endDrag() {
    const rd = this.roomGizmoDrag;
    if (rd) {
      this.roomGizmoDrag = null;
      this.suppressPick = true;
      const r = this.roomGizmo;
      if (r && rd.moved) this.onRoomItemMove(r.item.id, r.u0, r.elev);
      return;
    }
    const st = this.dragStart;
    const t = this.gizmoTarget;
    const duplicate = this.dragDuplicate;
    this.dragStart = null;
    this.dragDuplicate = false;
    this.suppressPick = true;
    if (this.ghost) {
      this.ghost.removeFromParent();
      this.ghost = null;
    }
    if (duplicate) this.onDuplicateDrag(false);
    if (!st || !t) return;
    const moved = this.dragDelta.some((v) => v !== 0);
    // A click (no drag) on the centre of the gizmo of a unit: the corner under it can't be clicked, so
    // the corner menu opens for it (pick it for the snap, or put the origin there).
    if (!moved && t.kind === 'unit' && this.dragAxis === 'XYZ' && this.down && this.cornersEnabled) {
      const c = this.proxy.position;
      const at = { x: this.down.x, y: this.down.y };
      this.setMoveInfo(null);
      this.onCornerMenu(at, [Math.round(c.x * M), Math.round(c.y * M), Math.round(c.z * M)]);
      return;
    }
    // After release the distance stays as input fields (not for Alt-copies: the copy becomes the selection).
    this.setMoveInfo(moved && !duplicate ? { key: targetKey(t), target: t, delta: this.dragDelta, live: false } : null);
    if (moved) this.onMove(t, this.dragDelta, { duplicate });
    else st.obj.position.copy(st.objPos);
  }

  // ---------- move readout / exact distance ----------

  private setMoveInfo(info: { key: string; target: MoveTarget; delta: Vec3; live: boolean } | null) {
    const prev = this.moveInfo;
    this.moveInfo = info;
    if (!info) {
      this.moveBox.style.display = 'none';
      this.moveBox.replaceChildren();
      return;
    }
    const AX = ['X', 'Y', 'Z'] as const;
    const COLOR = ['#ff5a5a', '#4fd66a', '#4f8dff'];
    const axes = [0, 1, 2].filter((k) => info.delta[k] !== 0);
    if (info.live) {
      this.moveBox.className = 'move-box live';
      this.moveBox.innerHTML = axes.map((k) => `<span style="color:${COLOR[k]}">${AX[k]}</span> ${info.delta[k]! > 0 ? '+' : ''}${(info.delta[k]! / 1000).toFixed(1)} mm`).join('&nbsp;&nbsp;') || '0 mm';
      return;
    }
    if (prev && !prev.live && prev.key === info.key && this.moveBox.querySelector('input:focus')) return;
    this.moveBox.className = 'move-box edit';
    this.moveBox.replaceChildren();
    for (const k of axes) {
      const label = document.createElement('span');
      label.textContent = AX[k]!;
      label.style.color = COLOR[k]!;
      const input = document.createElement('input');
      input.className = 'input';
      input.value = formatMm(info.delta[k]!);
      input.title = 'Ukucajte tačan pomak u mm (Enter), npr. 150 ili 300/2';
      input.setAttribute('aria-label', `Pomak ${AX[k]} u mm`);
      input.addEventListener('focus', () => input.select());
      input.addEventListener('keydown', (e) => {
        e.stopPropagation();
        if (e.key === 'Escape') {
          input.value = formatMm(info.delta[k]!);
          input.blur();
        }
        if (e.key !== 'Enter') return;
        const v = evalMm(input.value);
        if (v === null || !Number.isFinite(v)) {
          input.classList.add('invalid');
          return;
        }
        const cur = this.moveInfo;
        if (!cur) return;
        const to = [...cur.delta] as Vec3;
        to[k] = Math.round(v * 1000); // evalMm gives mm
        if (to[k] === cur.delta[k]) return;
        const from = cur.delta;
        this.moveInfo = { ...cur, delta: to };
        input.blur();
        this.onExactMove(cur.target, from, to);
      });
      const unit = document.createElement('span');
      unit.className = 'faint';
      unit.textContent = 'mm';
      this.moveBox.append(label, input, unit);
    }
  }

  private positionMoveBox() {
    const info = this.moveInfo;
    const show = !!info && !!this.tc.object && !this.pinMoving && (info.live || (!!this.gizmoTarget && targetKey(this.gizmoTarget) === info.key));
    this.moveBox.style.display = show ? 'flex' : 'none';
    if (!show) return;
    const s = this.toScreen(this.proxy.position);
    this.moveBox.style.left = `${Math.round(s.x + 46)}px`;
    this.moveBox.style.top = `${Math.round(s.y - 64)}px`;
  }

  // ---------- flip orientation ----------

  /** Which flip buttons to show next to the gizmo (panels: x/y/z, cabinets: y). */
  setFlipAxes(axes: ('x' | 'y' | 'z')[]) {
    if (axes.join() === this.flipAxes.join()) return;
    this.flipAxes = axes;
    this.flipBar.replaceChildren();
    const color = { x: '#ff5a5a', y: '#4fd66a', z: '#4f8dff' } as const;
    const label = { x: 'Okreni oko X (90°)', y: 'Okreni oko Y (90°)', z: 'Okreni oko Z (90°)' } as const;
    for (const ax of axes) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'flip-btn';
      b.title = `Flip orientation — ${label[ax]}`;
      b.setAttribute('aria-label', label[ax]);
      b.style.color = color[ax];
      b.innerHTML = `${FLIP_ICON}<span>${ax.toUpperCase()}</span>`;
      b.addEventListener('click', (e) => {
        e.stopPropagation();
        if (this.gizmoTarget) this.onFlip(this.gizmoTarget, ax);
      });
      this.flipBar.appendChild(b);
    }
  }

  private positionFlipBar() {
    const show = !!this.tc.object && !this.dragStart && !this.pinMoving && this.flipAxes.length > 0;
    this.flipBar.style.display = show ? 'flex' : 'none';
    if (!show) return;
    const s = this.toScreen(this.proxy.position);
    this.flipBar.style.left = `${Math.round(s.x + 46)}px`;
    this.flipBar.style.top = `${Math.round(s.y + 34)}px`;
  }

  isDragging() {
    return !!this.dragStart;
  }

  // ---------- pin (anchor point) ----------

  /** Shows the pin of the selected panel/cabinet. `unitSize` (µm) is needed for cabinets. */
  setPin(target: MoveTarget | null, value: Pin | null, unitSize: Vec3 | null) {
    this.pin = target && value ? { target, value, unitSize } : null;
    if (!this.pin && this.pinMoving) this.endPinMove();
    this.placePin();
  }

  isPinMoving() {
    return this.pinMoving;
  }

  /** World position of a pin fraction for the current target (null if the object is not rendered). */
  private pinWorld(value: Pin): THREE.Vector3 | null {
    const pin = this.pin;
    if (!pin) return null;
    if (pin.target.kind === 'panel') {
      const partId = pin.target.partId;
      const mesh = this.meshes.find((m) => (m.userData as PickResult).partId === partId);
      if (!mesh) return null;
      const { width: w, height: h, depth: d } = (mesh.geometry as THREE.BoxGeometry).parameters;
      return new THREE.Vector3((value[0] - 0.5) * w, (value[1] - 0.5) * h, (value[2] - 0.5) * d).applyMatrix4(mesh.matrixWorld);
    }
    const local = this.unitGroups.get(pin.target.unitId)?.children[0];
    if (!local || !pin.unitSize) return null;
    return new THREE.Vector3((value[0] * pin.unitSize[0]) / M, (value[1] * pin.unitSize[1]) / M, (value[2] * pin.unitSize[2]) / M).applyMatrix4(local.matrixWorld);
  }

  private placePin() {
    const w = this.pin ? this.pinWorld(this.pin.value) : null;
    this.pinMarker.visible = !!w && !this.pinMoving;
    if (w) this.pinMarker.position.copy(w);
    if (!w) this.pinButton.style.display = 'none';
  }

  private toScreen(p: THREE.Vector3): { x: number; y: number } {
    const v = p.clone().project(this.camera);
    return { x: ((v.x + 1) / 2) * this.host.clientWidth, y: ((1 - v.y) / 2) * this.host.clientHeight };
  }

  /** "✥ Pomjeri pin" appears when the cursor is near the pin. */
  private pinButtonHover(e: PointerEvent) {
    if (!this.pinMarker.visible) {
      this.pinButton.style.display = 'none';
      return;
    }
    const rect = this.renderer.domElement.getBoundingClientRect();
    const s = this.toScreen(this.pinMarker.position);
    const d = Math.hypot(s.x - (e.clientX - rect.left), s.y - (e.clientY - rect.top));
    if (d <= 14) {
      this.pinButton.style.left = `${Math.round(s.x + 12)}px`;
      this.pinButton.style.top = `${Math.round(s.y - 36)}px`;
      this.pinButton.style.display = 'block';
    } else if (d > 60) {
      this.pinButton.style.display = 'none';
    }
  }

  private startPinMove() {
    if (!this.pin) return;
    this.pinMoving = true;
    this.pinCandidate = null;
    this.pinButton.style.display = 'none';
    this.pinMarker.visible = false;
    this.pinPreview.position.copy(this.pinMarker.position);
    this.pinPreview.visible = true;
    this.clearCorner();
    this.hoverMarker.visible = false;
    this.onPinState('moving');
  }

  /** Follows the mouse; snaps to the nearest corner or edge midpoint of the object within 22 px. */
  private pinMoveHover(e: PointerEvent) {
    const rect = this.renderer.domElement.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    let best: { pin: Pin; p: THREE.Vector3; d: number } | null = null;
    for (const cand of PIN_POSITIONS) {
      const p = this.pinWorld(cand);
      if (!p) continue;
      const s = this.toScreen(p);
      const d = Math.hypot(s.x - mx, s.y - my);
      if (d <= 22 && (!best || d < best.d)) best = { pin: cand, p, d };
    }
    this.pinCandidate = best?.pin ?? null;
    const mat = this.pinPreview.material as THREE.MeshBasicMaterial;
    if (best) {
      this.pinPreview.position.copy(best.p);
      mat.opacity = 1;
    } else {
      // Free follow: cursor ray on the camera-facing plane through the current pin.
      const ray = this.ray(e);
      const normal = this.camera.getWorldDirection(new THREE.Vector3());
      const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(normal, this.pinMarker.position);
      const hit = ray.ray.intersectPlane(plane, new THREE.Vector3());
      if (hit) this.pinPreview.position.copy(hit);
      mat.opacity = 0.45;
    }
    this.renderer.domElement.style.cursor = best ? 'crosshair' : 'move';
  }

  private endPinMove() {
    this.pinMoving = false;
    this.pinCandidate = null;
    this.pinPreview.visible = false;
    this.renderer.domElement.style.cursor = '';
    this.placePin();
    this.onPinState('idle');
  }

  cancelPinMove() {
    if (this.pinMoving) this.endPinMove();
  }

  // ---------- corner snap ----------

  /**
   * Sets what a corner snap moves. `sourcePartId` is the panel whose corners can be picked
   * (for a parametric cabinet the whole cabinet moves, but corners come from the clicked panel).
   */
  setMoveTarget(target: MoveTarget | null, sourcePartId: string | null) {
    const changed = JSON.stringify([target, sourcePartId]) !== JSON.stringify([this.moveTarget, this.sourcePartId]);
    this.moveTarget = target;
    this.sourcePartId = sourcePartId;
    if (changed) {
      this.clearCorner();
      this.clearEdges();
    }
  }
  private sourcePartId: string | null = null;

  /** Corner picking is disabled while previews move geometry away from its real place. */
  setCornersEnabled(on: boolean) {
    this.cornersEnabled = on;
    if (!on) {
      this.clearCorner();
      this.clearEdges();
    }
  }

  hasCornerSelection() {
    return !!this.cornerSource;
  }

  clearCorner() {
    if (!this.cornerSource && !this.sourceMarker.visible) return;
    this.cornerSource = null;
    this.sourceMarker.visible = false;
    this.onCornerState('idle');
  }

  private movingMeshes(t: MoveTarget) {
    return this.meshes.filter((m) => {
      const d = m.userData as PickResult;
      return t.kind === 'panel' ? d.partId === t.partId : d.cabinetId === t.unitId;
    });
  }

  private static corners(m: THREE.Mesh): THREE.Vector3[] {
    const { width: w, height: h, depth: d } = (m.geometry as THREE.BoxGeometry).parameters;
    const out: THREE.Vector3[] = [];
    for (const x of [-w / 2, w / 2]) for (const y of [-h / 2, h / 2]) for (const z of [-d / 2, d / 2]) out.push(new THREE.Vector3(x, y, z).applyMatrix4(m.matrixWorld));
    return out;
  }

  /** The 8 outer corners of a unit (its box in the room; units only turn by quarter turns). */
  private unitCorners(unitId: string): THREE.Vector3[] {
    const box = new THREE.Box3();
    for (const m of this.meshes) if ((m.userData as PickResult).cabinetId === unitId) box.union(new THREE.Box3().setFromObject(m));
    if (box.isEmpty()) return [];
    const out: THREE.Vector3[] = [];
    for (const x of [box.min.x, box.max.x]) for (const y of [box.min.y, box.max.y]) for (const z of [box.min.z, box.max.z]) out.push(new THREE.Vector3(x, y, z));
    return out;
  }

  /**
   * Corners that can be picked now: first on what moves (a unit: only its outer corners; a panel: its
   * corners), then — after one is chosen — on everything else (other units' outer corners or other
   * panels' corners) and the room's wall corners.
   */
  private cornerCandidates(source: boolean): THREE.Vector3[] {
    const t = this.moveTarget!;
    if (source) {
      if (t.kind === 'unit') return this.unitCorners(t.unitId);
      const moving = new Set(this.movingMeshes(t));
      return this.meshes.filter((m) => (this.sourcePartId ? (m.userData as PickResult).partId === this.sourcePartId : moving.has(m))).flatMap((m) => ThreeViewport.corners(m));
    }
    let points: THREE.Vector3[];
    if (t.kind === 'unit') {
      const ids = new Set(this.meshes.map((m) => (m.userData as PickResult).cabinetId).filter((id) => id !== t.unitId));
      points = [...ids].flatMap((id) => this.unitCorners(id));
    } else {
      const moving = new Set(this.movingMeshes(t));
      points = this.meshes.filter((m) => !moving.has(m)).flatMap((m) => ThreeViewport.corners(m));
    }
    for (const w of this.roomWalls) if (w.group.visible) points.push(...w.points);
    return points;
  }

  /** Nearest visible corner within 14 px of the cursor (see `cornerCandidates`). */
  private nearestCorner(e: { clientX: number; clientY: number }, source = !this.cornerSource): THREE.Vector3 | null {
    const points = this.cornerCandidates(source);
    const rect = this.renderer.domElement.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    let best: { p: THREE.Vector3; d: number } | null = null;
    const v = new THREE.Vector3();
    for (const c of points) {
      v.copy(c).project(this.camera);
      if (v.z > 1) continue;
      const sx = ((v.x + 1) / 2) * rect.width;
      const sy = ((1 - v.y) / 2) * rect.height;
      const d = Math.hypot(sx - mx, sy - my);
      if (d <= 14 && (!best || d < best.d) && this.cornerVisible(c)) best = { p: c, d };
    }
    return best?.p ?? null;
  }

  /** Right click on a corner of what moves: the editor offers a menu (pick it even under the gizmo, move the origin there). */
  onCornerMenu: (at: { x: number; y: number }, corner: Vec3) => void = () => {};

  /** Chooses a corner as the snap source (from the corner menu). */
  pickCornerSource(corner: Vec3) {
    if (!this.moveTarget) return;
    this.cornerSource = new THREE.Vector3(corner[0] / M, corner[1] / M, corner[2] / M);
    this.sourceMarker.position.copy(this.cornerSource);
    this.sourceMarker.visible = true;
    this.hoverMarker.visible = false;
    this.hoverCorner = null;
    this.onCornerState('selected');
  }

  private onContextMenu = (e: MouseEvent) => {
    // A right drag pans the view: no menu then.
    const moved = this.rightDown ? Math.hypot(e.clientX - this.rightDown.x, e.clientY - this.rightDown.y) : 0;
    this.rightDown = null;
    if (moved > 4 || !this.cornersEnabled || !this.moveTarget || this.regions || this.wallPick || this.cornerSource) return;
    const c = this.nearestCorner(e, true);
    if (!c) return;
    e.preventDefault();
    this.onCornerMenu({ x: e.clientX, y: e.clientY }, [Math.round(c.x * M), Math.round(c.y * M), Math.round(c.z * M)]);
  };

  /** A corner is visible if nothing is hit clearly in front of it on the way from the camera. */
  private cornerVisible(c: THREE.Vector3): boolean {
    const origin = this.camera instanceof THREE.OrthographicCamera ? c.clone().add(this.camera.getWorldDirection(new THREE.Vector3()).multiplyScalar(-50)) : this.camera.position.clone();
    const dir = c.clone().sub(origin);
    const dist = dir.length();
    const hit = new THREE.Raycaster(origin, dir.normalize(), 0, dist).intersectObjects(this.meshes, false)[0];
    return !hit || hit.distance >= dist - 0.003;
  }

  /** Handles a click while hovering a corner. Returns true if the click was consumed. */
  private cornerClick(): boolean {
    const t = this.moveTarget;
    if (!t) return false;
    if (!this.hoverCorner) {
      this.clearCorner();
      return false;
    }
    if (!this.cornerSource) {
      this.cornerSource = this.hoverCorner.clone();
      this.sourceMarker.position.copy(this.cornerSource);
      this.sourceMarker.visible = true;
      this.hoverMarker.visible = false;
      this.hoverCorner = null;
      this.onCornerState('selected');
      return true;
    }
    const d = this.hoverCorner.clone().sub(this.cornerSource);
    this.clearCorner();
    this.hoverMarker.visible = false;
    this.hoverCorner = null;
    this.onMove(t, [Math.round(d.x * M), Math.round(d.y * M), Math.round(d.z * M)]);
    return true;
  }

  // ---------- compartment pick ("Izaberi prostor") ----------

  /** Shows clickable compartment boxes (unit-local µm) of one unit; `null` ends the pick. */
  setRegionPick(unitId: string | null, boxes: { min: Vec3; max: Vec3 }[] = []) {
    this.regions = unitId ? { unitId, boxes } : null;
    this.hoverRegion = -1;
    if (!unitId) this.regionGuard = () => true;
    this.regionSelected.clear();
    this.clearCorner();
    this.clearEdges();
    this.hoverMarker.visible = false;
    this.placeEdgeBar(this.edgeBars.hover, null);
    this.attachRegions();
  }

  isRegionPicking() {
    return !!this.regions;
  }

  private attachRegions() {
    for (const m of this.regionMeshes) {
      m.removeFromParent();
      m.geometry.dispose();
      (m.material as THREE.Material).dispose();
    }
    this.regionMeshes = [];
    const local = this.regions && this.unitGroups.get(this.regions.unitId)?.children[0];
    if (!this.regions || !local) return;
    this.regions.boxes.forEach((b, i) => {
      const size = [0, 1, 2].map((k) => (b.max[k]! - b.min[k]!) / M) as [number, number, number];
      const m = new THREE.Mesh(new THREE.BoxGeometry(...size), new THREE.MeshBasicMaterial({ color: 0x4fd66a, transparent: true, opacity: 0.18, depthWrite: false }));
      m.position.set((b.min[0] + b.max[0]) / 2 / M, (b.min[1] + b.max[1]) / 2 / M, (b.min[2] + b.max[2]) / 2 / M);
      m.renderOrder = 8;
      m.userData = { region: i };
      const edges = new THREE.LineSegments(new THREE.EdgesGeometry(m.geometry), new THREE.LineBasicMaterial({ color: 0x4fd66a }));
      m.add(edges);
      local.add(m);
      this.regionMeshes.push(m);
    });
    local.updateMatrixWorld(true);
    this.paintRegions();
  }

  private regionHover(e: PointerEvent) {
    const hit = this.ray(e).intersectObjects(this.regionMeshes, false)[0];
    this.hoverRegion = hit ? (hit.object.userData as { region: number }).region : -1;
    this.ctrlHeld = e.ctrlKey || e.metaKey;
    this.paintRegions();
  }

  /** Picked regions (e.g. the openings one front will cover). */
  setRegionSelected(indices: number[]) {
    this.regionSelected = new Set(indices);
    this.paintRegions();
  }

  private paintRegions() {
    this.hoverBlocked = this.hoverRegion >= 0 && !this.regionGuard(this.hoverRegion, this.ctrlHeld);
    if (this.regions) this.renderer.domElement.style.cursor = this.hoverRegion < 0 ? '' : this.hoverBlocked ? 'not-allowed' : 'pointer';
    this.regionMeshes.forEach((m, i) => {
      const mat = m.material as THREE.MeshBasicMaterial;
      const sel = this.regionSelected.has(i);
      const blocked = i === this.hoverRegion && this.hoverBlocked;
      mat.color.set(blocked ? 0xef4444 : sel ? 0xd6a467 : 0x4fd66a);
      mat.opacity = i === this.hoverRegion ? 0.5 : sel ? 0.42 : 0.18;
      ((m.children[0] as THREE.LineSegments).material as THREE.LineBasicMaterial).color.set(blocked ? 0xef4444 : sel ? 0xd6a467 : 0x4fd66a);
    });
  }

  // ---------- edge pick (copy a dimension) ----------

  hasEdgeSelection() {
    return !!this.edgeSource;
  }

  clearEdges() {
    if (!this.edgeSource && !this.edgeTarget) return;
    this.edgeSource = this.edgeTarget = null;
    this.placeEdgeBar(this.edgeBars.source, null);
    this.placeEdgeBar(this.edgeBars.target, null);
    this.onEdgePick({ source: null, target: null });
  }

  /** World end points of a box edge: along `axis`, at the given ±1 sides of the other two axes. */
  private edgeEnds(ref: EdgeRef): [THREE.Vector3, THREE.Vector3] | null {
    const m = this.meshes.find((x) => (x.userData as PickResult).partId === ref.partId);
    if (!m) return null;
    const { width: w, height: h, depth: d } = (m.geometry as THREE.BoxGeometry).parameters;
    const half = [w / 2, h / 2, d / 2];
    const [u, v] = ([0, 1, 2] as const).filter((i) => i !== ref.axis) as [0 | 1 | 2, 0 | 1 | 2];
    const p = [0, 0, 0];
    p[u] = ref.side[0] * half[u]!;
    p[v] = ref.side[1] * half[v]!;
    const a = [...p] as [number, number, number];
    const b = [...p] as [number, number, number];
    a[ref.axis] = -half[ref.axis]!;
    b[ref.axis] = half[ref.axis]!;
    return [new THREE.Vector3(...a).applyMatrix4(m.matrixWorld), new THREE.Vector3(...b).applyMatrix4(m.matrixWorld)];
  }

  private placeEdgeBar(bar: THREE.Mesh, ref: EdgeRef | null) {
    const ends = ref && this.edgeEnds(ref);
    bar.visible = !!ends;
    if (!ends) return;
    const [a, b] = ends;
    bar.position.copy(a).add(b).multiplyScalar(0.5);
    bar.scale.x = a.distanceTo(b);
    bar.quaternion.setFromUnitVectors(new THREE.Vector3(1, 0, 0), b.clone().sub(a).normalize());
  }

  /**
   * Nearest visible edge within 7 px: before a source is chosen only on the selected panel, then on
   * any panel (except the source edge itself).
   */
  private nearestEdge(e: PointerEvent): EdgeRef | null {
    const candidates = this.edgeSource ? this.meshes : this.meshes.filter((m) => (m.userData as PickResult).partId === this.sourcePartId);
    const rect = this.renderer.domElement.getBoundingClientRect();
    const mouse = new THREE.Vector2(e.clientX - rect.left, e.clientY - rect.top);
    const scr = (p: THREE.Vector3) => {
      const v = p.clone().project(this.camera);
      return v.z > 1 ? null : new THREE.Vector2(((v.x + 1) / 2) * rect.width, ((1 - v.y) / 2) * rect.height);
    };
    let best: { ref: EdgeRef; d: number } | null = null;
    for (const m of candidates) {
      const partId = (m.userData as PickResult).partId;
      for (const axis of [0, 1, 2] as const)
        for (const s0 of [-1, 1] as const)
          for (const s1 of [-1, 1] as const) {
            const ref: EdgeRef = { partId, axis, side: [s0, s1] };
            if (this.edgeSource && sameEdge(ref, this.edgeSource)) continue;
            const ends = this.edgeEnds(ref);
            if (!ends) continue;
            const A = scr(ends[0]);
            const B = scr(ends[1]);
            if (!A || !B) continue;
            const ab = B.clone().sub(A);
            const t = ab.lengthSq() ? Math.max(0, Math.min(1, mouse.clone().sub(A).dot(ab) / ab.lengthSq())) : 0;
            const d = A.clone().add(ab.multiplyScalar(t)).distanceTo(mouse);
            if (d > 7 || (best && d >= best.d)) continue;
            if (this.cornerVisible(ends[0].clone().lerp(ends[1], t))) best = { ref, d };
          }
    }
    return best?.ref ?? null;
  }

  /** Click with an edge under the cursor: first picks the source, then the target. */
  private edgeClick(): boolean {
    if (!this.hoverEdge) {
      this.clearEdges();
      return false;
    }
    if (!this.edgeSource) {
      this.clearCorner();
      this.edgeSource = this.hoverEdge;
      this.placeEdgeBar(this.edgeBars.source, this.edgeSource);
    } else {
      this.edgeTarget = this.hoverEdge;
      this.placeEdgeBar(this.edgeBars.target, this.edgeTarget);
    }
    this.hoverEdge = null;
    this.placeEdgeBar(this.edgeBars.hover, null);
    this.onEdgePick({ source: this.edgeSource, target: this.edgeTarget });
    return true;
  }

  // ---------- links ----------

  setLinkLines(lines: LinkLine[], visible: boolean) {
    this.linkGroup.traverse((o) => {
      if (o instanceof THREE.Line || o instanceof THREE.Points) {
        o.geometry.dispose();
        (o.material as THREE.Material).dispose();
      }
    });
    this.linkGroup.clear();
    this.linkGroup.visible = visible;
    for (const l of lines) {
      const geo = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(l.from[0] / M, l.from[1] / M, l.from[2] / M), new THREE.Vector3(l.to[0] / M, l.to[1] / M, l.to[2] / M)]);
      const line = new THREE.Line(geo, new THREE.LineDashedMaterial({ color: l.mode === 'equal' ? 0x4fc3f7 : 0xffa94d, dashSize: 0.03, gapSize: 0.02, depthTest: false, transparent: true }));
      line.computeLineDistances();
      line.renderOrder = 9;
      this.linkGroup.add(line);
      // End markers make the (1 px) dashed line easy to spot.
      const dots = new THREE.Points(geo, new THREE.PointsMaterial({ color: l.mode === 'equal' ? 0x4fc3f7 : 0xffa94d, size: 9, sizeAttenuation: false, depthTest: false }));
      dots.renderOrder = 9;
      this.linkGroup.add(dots);
    }
  }

  // ---------- materials ----------

  /** Replaces the decor table (catalog + demo). Materials are rebuilt lazily. */
  setDecors(list: DecorView[]) {
    const next = new Map(list.map((d) => [d.id, d]));
    const sig = (m: Map<string, DecorView>) => JSON.stringify([...m.values()]);
    if (sig(next) === sig(this.decors)) return;
    this.decors = next;
    this.materials.forEach((m) => m.dispose());
    this.materials.clear();
  }

  /**
   * UVs are in metres (1 UV unit = 1000 mm, see setPhysicalUVs); `repeat` = 1000 / tile size in mm,
   * so a texture keeps its physical scale on every panel size.
   */
  private repeated(tex: THREE.Texture, uMm: number, vMm: number): THREE.Texture {
    const t = tex.clone();
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.repeat.set(1000 / uMm, 1000 / vMm);
    t.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
    t.needsUpdate = true;
    return t;
  }

  private loaded(url: string, colorSpace: THREE.ColorSpace): THREE.Texture {
    const key = `${url}|${colorSpace}`;
    let t = this.textures.get(key);
    if (!t) {
      t = this.loader.load(url, (tex) => {
        tex.needsUpdate = true;
        this.materials.forEach((m) => (m.needsUpdate = true));
      });
      t.colorSpace = colorSpace;
      this.textures.set(key, t);
    }
    return t;
  }

  private procedural(decor: DecorView): THREE.Texture | null {
    if (!decor.procedural || decor.procedural.kind === 'solid') return null;
    const cached = this.textures.get(`proc:${decor.id}`);
    if (cached) return cached;
    const c = document.createElement('canvas');
    c.width = 512;
    c.height = 512;
    const g = c.getContext('2d')!;
    g.fillStyle = decor.baseColor;
    g.fillRect(0, 0, 512, 512);
    // Deterministic pseudo-random so the texture is stable across reloads.
    let seed = [...decor.id].reduce((a, ch) => a + ch.charCodeAt(0), 7);
    const rnd = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
    if (decor.procedural.kind === 'wood') {
      // Grain runs along texture U (horizontal lines). UVs map U to the part's grain direction.
      for (let i = 0; i < 160; i++) {
        const y = rnd() * 512;
        g.strokeStyle = decor.procedural.ringColor ?? '#000';
        g.globalAlpha = 0.08 + rnd() * 0.18;
        g.lineWidth = 0.5 + rnd() * 2.5;
        g.beginPath();
        g.moveTo(0, y);
        for (let x = 0; x <= 512; x += 32) g.lineTo(x, y + Math.sin((x / 512) * Math.PI * 2 * (1 + rnd())) * 3);
        g.stroke();
      }
    } else {
      for (let i = 0; i < 9000; i++) {
        g.globalAlpha = 0.05 + rnd() * 0.08;
        g.fillStyle = rnd() > 0.5 ? '#000' : '#fff';
        g.fillRect(rnd() * 512, rnd() * 512, 1 + rnd() * 2, 1 + rnd() * 2);
      }
    }
    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    this.textures.set(`proc:${decor.id}`, tex);
    return tex;
  }

  private material(decorId: string, xray: boolean): THREE.MeshStandardMaterial {
    const key = `${decorId}|${xray}`;
    const cached = this.materials.get(key);
    if (cached) return cached;
    const decor = this.decors.get(decorId);
    let map: THREE.Texture | null = null;
    let normalMap: THREE.Texture | null = null;
    let roughnessMap: THREE.Texture | null = null;
    if (decor?.maps.baseColor) {
      const m = decor.maps.baseColor;
      map = this.repeated(this.loaded(m.url, THREE.SRGBColorSpace), m.uMm, m.vMm);
    } else if (decor) {
      const proc = this.procedural(decor);
      const tile = decor.procedural?.scaleMm ?? 600;
      if (proc) map = this.repeated(proc, tile, tile);
    }
    // Data maps are linear (no sRGB decode). Normal convention: OpenGL (+Y), same as Blender.
    if (decor?.maps.normal) normalMap = this.repeated(this.loaded(decor.maps.normal.url, THREE.NoColorSpace), decor.maps.normal.uMm, decor.maps.normal.vMm);
    if (decor?.maps.roughness) roughnessMap = this.repeated(this.loaded(decor.maps.roughness.url, THREE.NoColorSpace), decor.maps.roughness.uMm, decor.maps.roughness.vMm);
    const m = new THREE.MeshStandardMaterial({
      // Unknown decor is shown magenta on purpose so it is noticed, never silently substituted.
      color: decor ? (map ? '#ffffff' : decor.baseColor) : '#ff00ff',
      roughness: decor?.roughness ?? 0.7,
      metalness: 0,
      map,
      normalMap,
      roughnessMap,
      transparent: xray,
      opacity: xray ? 0.28 : 1,
      depthWrite: !xray,
    });
    this.materials.set(key, m);
    return m;
  }

  // ---------- scene ----------

  private clearContent() {
    this.unitGroups.clear();
    this.partHolders.clear();
    this.content.traverse((o) => {
      if (o instanceof THREE.Mesh || o instanceof THREE.LineSegments) o.geometry.dispose();
      if (o instanceof THREE.LineSegments) (o.material as THREE.Material).dispose();
      if (o instanceof CSS2DObject) o.element.remove();
    });
    this.content.clear();
    this.meshes = [];
    this.roomWalls = [];
    this.roomItems.clear();
  }

  /** Room walls with their inner-face line and outward normal (metres), for the cutaway. */
  private roomWalls: { group: THREE.Group; mesh: THREE.Mesh; items: THREE.Mesh[]; lineId: string; points: THREE.Vector3[]; x: number; z: number; nx: number; nz: number }[] = [];
  private roomItems = new Map<string, RoomItemEntry>();
  /** Dimension lines for the items of the selected wall. */
  private roomDims = false;
  /** Items can be dragged along their wall. */
  private roomEditable = false;
  private itemDrag: { r: RoomItemEntry; grabU: number; grabY: number; sx: number; sy: number; moved: boolean } | null = null;

  setRoomEditing(o: { dims: boolean; editable: boolean }) {
    this.roomDims = o.dims;
    this.roomEditable = o.editable;
  }

  /** Point on the inner face plane of a wall under the pointer: u (µm from the wall start) and height (µm). */
  private wallPoint(e: PointerEvent, w: RoomWallBuild): { u: number; y: number } | null {
    const m = toMetres;
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(new THREE.Vector3(w.nx, 0, w.nz), new THREE.Vector3(m(w.ax), 0, m(w.az)));
    const p = this.ray(e).ray.intersectPlane(plane, new THREE.Vector3());
    if (!p) return null;
    return { u: ((p.x - m(w.ax)) * w.ux + (p.z - m(w.az)) * w.uz) * 1e6, y: p.y * 1e6 };
  }

  private onItemDown = (e: PointerEvent) => {
    if (e.button !== 0 || !this.roomEditable || this.wallPick || this.regions || this.pinMoving || this.cornerSource || this.tc.axis || e.shiftKey || e.ctrlKey || e.metaKey) return;
    const hit = this.ray(e).intersectObjects([...this.meshes, ...this.roomPickables()], false)[0];
    const id = (hit?.object.userData as { itemId?: string } | undefined)?.itemId;
    const r = id ? this.roomItems.get(id) : undefined;
    const p = r && this.wallPoint(e, r.w);
    if (!r || !p) return;
    e.stopImmediatePropagation();
    e.preventDefault();
    this.controls.enabled = false;
    this.itemDrag = { r, grabU: p.u - r.u0, grabY: p.y - r.elev, sx: e.clientX, sy: e.clientY, moved: false };
    window.addEventListener('pointermove', this.onItemMove);
    window.addEventListener('pointerup', this.onItemUp);
  };

  private onItemMove = (e: PointerEvent) => {
    const d = this.itemDrag;
    if (!d) return;
    if (!d.moved && Math.hypot(e.clientX - d.sx, e.clientY - d.sy) < 4) return;
    d.moved = true;
    const p = this.wallPoint(e, d.r.w);
    if (!p) return;
    // 10 mm steps; Shift = 1 mm.
    this.moveItemTo(d.r, p.u - d.grabU, p.y - d.grabY, e.shiftKey ? 1000 : 10_000);
    if (this.roomGizmo === d.r) this.proxy.position.copy(d.r.box.position);
    this.renderer.domElement.style.cursor = 'grabbing';
  };

  /** Moves an item (live) to a start along its wall and a height, rounded and kept inside the wall. */
  private moveItemTo(r: RoomItemEntry, u0Raw: number, elevRaw: number, step: number) {
    const { w, item, h } = r;
    const round = (x: number) => Math.round(x / step) * step;
    const u0 = Math.max(0, Math.min(round(u0Raw), Math.round(w.length - item.width)));
    // Doors stay on the floor; the others go up to the (lowest) wall top over them.
    const top = Math.min(wallTopAt(w, Math.max(0, u0)), wallTopAt(w, Math.min(w.length, u0 + item.width)));
    const elev = item.kind === 'door' ? item.elevation : Math.max(0, Math.min(round(elevRaw), Math.round(top - h)));
    r.u0 = u0;
    r.elev = elev;
    const uc = u0 + item.width / 2;
    const m = toMetres;
    r.box.position.set(m(w.ax + w.ux * uc + w.nx * r.v), m(elev + h / 2), m(w.az + w.uz * uc + w.nz * r.v));
    this.updateItemDims(r);
  }

  /** Gizmo of the selected room item, turned with its wall: only along the wall (X), up (Y) and in the wall plane. */
  private roomGizmo: RoomItemEntry | null = null;
  private roomGizmoDrag: { p0: THREE.Vector3; u0: number; elev: number; moved: boolean } | null = null;

  setRoomGizmo(on: boolean) {
    if (this.roomGizmoDrag || this.dragStart) return;
    const r = on && this.roomSel?.itemId ? this.roomItems.get(this.roomSel.itemId) : undefined;
    if (!r) {
      if (this.roomGizmo) {
        this.roomGizmo = null;
        this.proxy.rotation.set(0, 0, 0);
        this.tc.space = 'world';
        this.tc.showZ = true;
        if (!this.gizmoTarget) this.tc.detach();
      }
      return;
    }
    this.roomGizmo = r;
    this.proxy.position.copy(r.box.position);
    this.proxy.rotation.set(0, r.box.rotation.y, 0);
    this.tc.space = 'local';
    // No Z: the handles that leave the wall plane (Z, XZ, YZ, centre) are hidden.
    this.tc.showZ = false;
    this.tc.attach(this.proxy);
  }

  private onItemUp = () => {
    const d = this.itemDrag;
    window.removeEventListener('pointermove', this.onItemMove);
    window.removeEventListener('pointerup', this.onItemUp);
    this.itemDrag = null;
    this.controls.enabled = true;
    this.renderer.domElement.style.cursor = '';
    if (!d) return;
    if (d.moved) this.onRoomItemMove(d.r.item.id, d.r.u0, d.r.elev);
    else this.onRoomPick({ lineId: d.r.w.lineId, itemId: d.r.item.id });
  };

  /** Dimension lines of an item: from its wall end to its near edge, and from the floor to its bottom. */
  private updateItemDims(r: RoomItemEntry) {
    if (r.dims) {
      r.parent.remove(r.dims);
      r.dims.traverse((o) => {
        if (o instanceof THREE.LineSegments) o.geometry.dispose();
        if (o instanceof CSS2DObject) o.element.remove();
      });
      r.dims = null;
    }
    const show = (this.roomDims && this.roomSel?.lineId === r.w.lineId) || this.itemDrag?.r === r;
    if (!show) return;
    const { w, item, u0, elev, h } = r;
    const m = toMetres;
    const v = -8000; // 8 mm in front of the inner face
    const P = (u: number, y: number) => new THREE.Vector3(m(w.ax + w.ux * u + w.nx * v), m(y), m(w.az + w.uz * u + w.nz * v));
    const g = new THREE.Group();
    const pts: THREE.Vector3[] = [];
    const t = 30_000; // end ticks ±30 mm
    const label = (at: THREE.Vector3, text: string, title: string) => {
      const el = document.createElement('div');
      el.className = 'room-dim3d';
      el.textContent = text;
      el.title = title;
      const o = new CSS2DObject(el);
      o.position.copy(at);
      g.add(o);
    };
    const yMid = elev + h / 2;
    const [ua, ub] = item.from === 'a' ? [0, u0] : [u0 + item.width, w.length];
    if (ub - ua > 500) {
      pts.push(P(ua, yMid), P(ub, yMid), P(ua, yMid - t), P(ua, yMid + t), P(ub, yMid - t), P(ub, yMid + t));
      label(P((ua + ub) / 2, yMid), formatMm(Math.round(ub - ua)), item.from === 'a' ? 'Od početka zida' : 'Od kraja zida');
    }
    const uc = u0 + item.width / 2;
    if (elev > 500) {
      pts.push(P(uc, 0), P(uc, elev), P(uc - t, 0), P(uc + t, 0), P(uc - t, elev), P(uc + t, elev));
      label(P(uc, elev / 2), formatMm(Math.round(elev)), 'Od poda');
    }
    if (!pts.length) return;
    const lines = new THREE.LineSegments(new THREE.BufferGeometry().setFromPoints(pts), DIM_MAT);
    lines.renderOrder = 11;
    g.add(lines);
    r.parent.add(g);
    r.dims = g;
  }

  /** Selected wall / item of the room (outliner or a click in 3D). */
  private roomSel: { lineId: string; itemId: string | null } | null = null;

  setRoomSelection(sel: { lineId: string; itemId?: string | null } | null) {
    this.roomSel = sel ? { lineId: sel.lineId, itemId: sel.itemId ?? null } : null;
  }

  /** Walls and items that can be clicked: not the cut-away ones (a selected cut-away wall is only a ghost). */
  private roomPickables(): THREE.Mesh[] {
    if (this.wallPick) return [];
    const out: THREE.Mesh[] = [];
    for (const w of this.roomWalls) if (w.group.visible && w.mesh.material !== ROOM_MAT.wallGhost) out.push(w.mesh, ...w.items);
    return out;
  }
  /** "Montiraj na zid": the next click on a wall picks it. */
  private wallPick: ((lineId: string) => void) | null = null;
  private hoverWall: string | null = null;

  setWallPick(cb: ((lineId: string) => void) | null) {
    this.wallPick = cb;
    this.setHoverWall(null);
    this.renderer.domElement.style.cursor = cb ? 'pointer' : '';
  }

  private setHoverWall(lineId: string | null) {
    this.hoverWall = lineId;
    for (const w of this.roomWalls) w.mesh.material = w.lineId === lineId ? ROOM_MAT.wallHover : this.wallMat(w.lineId, false);
  }

  private wallMat(lineId: string, cut: boolean): THREE.Material {
    const sel = this.roomSel?.lineId === lineId && !this.roomSel.itemId;
    return cut ? ROOM_MAT.wallGhost : sel ? ROOM_MAT.wallSel : ROOM_MAT.wall;
  }

  private wallUnder(e: PointerEvent): string | null {
    const meshes = this.roomWalls.filter((w) => w.group.visible).map((w) => w.mesh);
    const hit = this.ray(e).intersectObjects(meshes, false)[0];
    return hit ? ((hit.object.userData as { lineId?: string }).lineId ?? null) : null;
  }

  /** Walls between the camera and the room are hidden (with their windows and sockets), so the room is seen from outside. */
  private cutawayWalls() {
    if (!this.roomWalls.length) return;
    const c = this.camera.position;
    const d = this.camera.getWorldDirection(new THREE.Vector3());
    const ortho = (this.camera as THREE.Camera & { isOrthographicCamera?: boolean }).isOrthographicCamera;
    for (const w of this.roomWalls) {
      // Perspective: the camera is outside this wall. Orthographic: the view looks through it from outside.
      const outside = ortho ? d.x * w.nx + d.z * w.nz < -0.2 : (c.x - w.x) * w.nx + (c.z - w.z) * w.nz > 0;
      // The selected wall (or the wall of the selected item) stays as a see-through ghost.
      const keep = outside && this.roomSel?.lineId === w.lineId;
      w.group.visible = !outside || keep;
      if (w.lineId !== this.hoverWall) w.mesh.material = this.wallMat(w.lineId, keep);
    }
  }

  /** Walls (with sloped tops and openings), floor and installations of the room; not pickable. */
  private roomGroup(plan: RoomPlan): THREE.Group {
    const g = new THREE.Group();
    g.name = 'room';
    const r = buildRoom(plan);
    const m = (v: number) => toMetres(v);
    const wallGroups = new Map<string, THREE.Group>();
    for (const w of r.walls) {
      // Built between the points (inner face), then the end vertices are slid along the wall with the
      // depth so the outer face reaches its mitred corner (extA / extB).
      const shape = new THREE.Shape();
      const u0 = 0;
      const u1 = w.length;
      shape.moveTo(m(u0), 0);
      shape.lineTo(m(u1), 0);
      shape.lineTo(m(u1), m(wallTopAt(w, u1)));
      shape.lineTo(m(u0), m(wallTopAt(w, u0)));
      shape.closePath();
      for (const h of w.holes) {
        const hole = new THREE.Path();
        hole.moveTo(m(h.u0), m(h.y0));
        hole.lineTo(m(h.u0), m(h.y1));
        hole.lineTo(m(h.u1), m(h.y1));
        hole.lineTo(m(h.u1), m(h.y0));
        hole.closePath();
        shape.holes.push(hole);
      }
      const X = new THREE.Vector3(w.ux, 0, w.uz);
      const Y = new THREE.Vector3(0, 1, 0);
      const n = new THREE.Vector3(w.nx, 0, w.nz);
      const right = new THREE.Vector3().crossVectors(X, Y).dot(n) > 0;
      const geo = new THREE.ExtrudeGeometry(shape, { depth: m(w.thickness), bevelEnabled: false });
      {
        const pos = geo.getAttribute('position') as THREE.BufferAttribute;
        const L = m(w.length);
        const T = m(w.thickness) || 1;
        const tops = [m(wallTopAt(w, 0)), m(wallTopAt(w, w.length))];
        const eps = 1e-7;
        for (let i = 0; i < pos.count; i++) {
          const x = pos.getX(i);
          const y = pos.getY(i);
          const end = Math.abs(x) < eps ? 0 : Math.abs(x - L) < eps ? 1 : -1;
          // Only the outline's end corners (floor / top), not the edges of an opening at the end.
          if (end < 0 || !(Math.abs(y) < eps || Math.abs(y - tops[end]!) < eps)) continue;
          // Depth from the inner face: local z runs outwards (z / T), or inwards for a left-handed wall (translated below).
          const f = right ? pos.getZ(i) / T : 1 - pos.getZ(i) / T;
          pos.setX(i, end === 0 ? -m(w.extA) * f : L + m(w.extB) * f);
        }
        pos.needsUpdate = true;
        geo.computeVertexNormals();
      }
      // Local X along the wall, Y up, Z through the thickness (outwards, away from the room).
      if (!right) geo.translate(0, 0, -m(w.thickness));
      const mesh = new THREE.Mesh(geo, this.wallMat(w.lineId, false));
      mesh.matrixAutoUpdate = false;
      mesh.matrix.makeBasis(X, Y, right ? n : n.clone().negate()).setPosition(m(w.ax), 0, m(w.az));
      mesh.userData = { lineId: w.lineId };
      if (this.roomSel?.lineId === w.lineId && !this.roomSel.itemId) {
        // Selected wall: green edges, drawn over everything (also when the wall is cut away).
        const edges = new THREE.LineSegments(new THREE.EdgesGeometry(geo), new THREE.LineBasicMaterial({ color: SEL_UNIT, depthTest: false, transparent: true }));
        edges.renderOrder = 10;
        mesh.add(edges);
      }
      const wg = new THREE.Group();
      wg.add(mesh);
      wallGroups.set(w.lineId, wg);
      // Snap points: inner corners at the floor and the top, outer corners, corners of the openings.
      const pt = (u: number, y: number, v: number) => new THREE.Vector3(m(w.ax + w.ux * u + w.nx * v), m(y), m(w.az + w.uz * u + w.nz * v));
      const points = [pt(0, 0, 0), pt(w.length, 0, 0), pt(0, w.hA, 0), pt(w.length, w.hB, 0)];
      for (const u of [-w.extA, w.length + w.extB]) points.push(pt(u, 0, w.thickness), pt(u, wallTopAt(w, u), w.thickness));
      for (const h of w.holes) points.push(pt(h.u0, h.y0, 0), pt(h.u1, h.y0, 0), pt(h.u0, h.y1, 0), pt(h.u1, h.y1, 0));
      this.roomWalls.push({ group: wg, mesh, items: [], lineId: w.lineId, points, x: m(w.ax), z: m(w.az), nx: w.nx, nz: w.nz });
      g.add(wg);
    }
    if (r.floor) {
      const s = new THREE.Shape(r.floor.map((p) => new THREE.Vector2(m(p.x), m(p.z))));
      const floor = new THREE.Mesh(new THREE.ShapeGeometry(s), ROOM_MAT.floor);
      floor.rotation.x = Math.PI / 2;
      floor.position.y = -0.0005;
      g.add(floor);
    }
    for (const { item, wall: w, u0, u1 } of r.items) {
      const uc = (u0 + u1) / 2;
      const thick = item.kind === 'window' ? 20_000 : item.kind === 'door' ? 40_000 : item.kind === 'socket' ? 12_000 : 60_000;
      // Openings: a pane / leaf in the middle of the wall; installations stick out of the inner face.
      const v = item.kind === 'window' ? w.thickness / 2 : item.kind === 'door' ? thick / 2 : -thick / 2;
      // Openings are cut to the (possibly sloped) wall top, like their hole.
      const top = item.kind === 'window' || item.kind === 'door' ? Math.min(item.elevation + item.height, Math.min(wallTopAt(w, Math.max(0, u0)), wallTopAt(w, Math.min(w.length, u1))) - 1000) : item.elevation + item.height;
      const h = top - item.elevation;
      if (h <= 0) continue;
      const box = new THREE.Mesh(new THREE.BoxGeometry(m(u1 - u0), m(h), m(thick)), ROOM_MAT[item.kind === 'window' ? 'glass' : item.kind]);
      box.position.set(m(w.ax + w.ux * uc + w.nx * v), m(item.elevation + h / 2), m(w.az + w.uz * uc + w.nz * v));
      box.rotation.y = Math.atan2(-w.uz, w.ux);
      box.userData = { lineId: w.lineId, itemId: item.id };
      const parent = wallGroups.get(w.lineId) ?? g;
      parent.add(box);
      this.roomWalls.find((x) => x.lineId === w.lineId)?.items.push(box);
      const entry: RoomItemEntry = { box, w, item, u0, elev: item.elevation, h, v, parent, dims: null };
      this.roomItems.set(item.id, entry);
      this.updateItemDims(entry);
      if (this.roomSel?.itemId === item.id) {
        // Selected item: green outline drawn over the wall, plus a light fill.
        const edges = new THREE.LineSegments(new THREE.EdgesGeometry(box.geometry), new THREE.LineBasicMaterial({ color: SEL_PART, depthTest: false }));
        edges.renderOrder = 10;
        const fill = new THREE.Mesh(box.geometry, SEL_PART_FILL);
        fill.scale.setScalar(1.02);
        fill.renderOrder = 9;
        box.add(edges, fill);
      }
    }
    return g;
  }

  /** Rebuilds the scene from the evaluated model. Cheap enough for hundreds of parts; instancing is a later optimisation. */
  setScene(doc: ProjectDocument, ev: EvaluatedProject, edgeDecisions: Map<string, Record<EdgeId, string>>, opts: ViewOptions) {
    this.clearContent();
    this.grid.visible = opts.grid;
    const bounds = new THREE.Box3();
    for (const cab of ev.cabinets) {
      const inst = cab.instance;
      if (inst.hidden) continue;
      const group = new THREE.Group();
      const [px, py, pz] = inst.transform.position;
      group.position.set(toMetres(px), toMetres(py), toMetres(pz));
      group.rotation.y = (-inst.transform.rotationY * Math.PI) / 180;
      const local = new THREE.Group();
      if (inst.transform.mirrored) {
        // Mirror about the cabinet's own width; local manufacturing dimensions are untouched.
        local.scale.x = -1;
        local.position.x = toMetres(cab.result.bounds.size[0]);
      }
      group.add(local);
      this.unitGroups.set(inst.id, group);
      const size = cab.result.bounds.size;
      const center = new THREE.Vector3(toMetres(size[0]) / 2, toMetres(size[1]) / 2, toMetres(size[2]) / 2);
      const cabSelected = this.selection.cabinetIds.has(inst.id);

      for (const part of cab.result.parts) {
        const mesh = this.partMesh(doc, inst.materials, part, opts.xray);
        mesh.userData = { cabinetId: inst.id, partId: part.id } satisfies PickResult;
        const holder = new THREE.Group();
        holder.add(mesh);
        this.applyPreview(holder, mesh, part, inst.transform.mirrored, cab.kind === 'cabinet' ? cab.instance.params.drawerSystem.nominalLength : cab.result.bounds.size[2], opts.openFronts);
        if (opts.exploded) {
          const d = mesh.position.clone().sub(center).multiplyScalar(0.35);
          holder.position.add(d);
        }
        const isSel = this.selection.partId === part.id || this.selection.extra.has(part.id);
        const shapeGeo = (mesh.getObjectByName('cnc-shape') as THREE.Mesh | undefined)?.geometry ?? mesh.geometry;
        // A component selected inside the unit: the unit's lines get darker so the component stands out.
        const unitColor = this.selection.partId || this.selection.extra.size ? SEL_UNIT_DIM : SEL_UNIT;
        const outline = new THREE.LineSegments(
          new THREE.EdgesGeometry(shapeGeo),
          new THREE.LineBasicMaterial({ color: isSel ? SEL_PART : cabSelected ? unitColor : 0x1a1d21, transparent: true, opacity: isSel || cabSelected ? 1 : 0.55, depthTest: !isSel }),
        );
        outline.position.copy(mesh.position);
        outline.rotation.copy(mesh.rotation);
        if (isSel) {
          // Drawn over everything, with a light fill, so it is visible inside the carcass too.
          outline.renderOrder = 10;
          const fill = new THREE.Mesh(shapeGeo, SEL_PART_FILL);
          fill.position.copy(mesh.position);
          fill.rotation.copy(mesh.rotation);
          fill.scale.setScalar(1.002);
          fill.renderOrder = 9;
          fill.userData.selFill = true;
          holder.add(fill);
        }
        holder.add(outline);
        if (opts.edges || isSel) {
          const dec = edgeDecisions.get(part.id);
          if (dec) this.addEdgeStrips(holder, mesh, part, dec);
        }
        if (opts.labels && (cabSelected || this.selection.cabinetIds.size === 0)) {
          const el = document.createElement('div');
          el.className = 'part-label';
          el.textContent = part.localKey;
          const label = new CSS2DObject(el);
          label.position.copy(mesh.position);
          holder.add(label);
        }
        local.add(holder);
        this.partHolders.set(part.id, holder);
        this.meshes.push(mesh);
      }
      this.content.add(group);
      if (cabSelected) {
        // Box around the selected unit.
        group.updateMatrixWorld(true);
        const bb = new THREE.Box3().setFromObject(local).expandByScalar(0.006);
        if (!bb.isEmpty()) this.content.add(new THREE.Box3Helper(bb, new THREE.Color(SEL_UNIT)));
      }
    }
    if (opts.room && doc.roomPlan?.wallHeight) this.content.add(this.roomGroup(doc.roomPlan));
    this.content.updateMatrixWorld(true);
    this.attachRegions();
    // Keep picked edges on the (possibly resized) panels.
    this.placeEdgeBar(this.edgeBars.source, this.edgeSource);
    this.placeEdgeBar(this.edgeBars.target, this.edgeTarget);
    this.placeEdgeBar(this.edgeBars.hover, null);
    for (const m of this.meshes) bounds.expandByObject(m);
    if (!bounds.isEmpty()) this.lastBounds.copy(bounds);
  }

  private partMesh(doc: ProjectDocument, overrides: Partial<Record<string, string>>, part: PanelPart, xray: boolean): THREE.Mesh {
    const box = partBox(part);
    const [sx, sy, sz] = box.size.map(toMetres) as [number, number, number];
    if (!part.inCutList && part.localKey.startsWith('nogica-')) {
      // Legs: dark round feet (hardware, not boards).
      const leg = new THREE.Mesh(new THREE.CylinderGeometry(sx / 2, sx / 2, sy, 20), LEG_MAT);
      leg.castShadow = leg.receiveShadow = !xray;
      leg.position.set(toMetres(box.min[0]) + sx / 2, toMetres(box.min[1]) + sy / 2, toMetres(box.min[2]) + sz / 2);
      return leg;
    }
    const geo = new THREE.BoxGeometry(sx, sy, sz);
    const decorId = overrides[part.materialSlot] ?? doc.materials[part.materialSlot];
    setPhysicalUVs(geo, part, 1000);
    const mesh = new THREE.Mesh(geo, this.material(decorId, xray));
    mesh.castShadow = !xray;
    mesh.receiveShadow = !xray;
    mesh.position.set(toMetres(box.min[0]) + sx / 2, toMetres(box.min[1]) + sy / 2, toMetres(box.min[2]) + sz / 2);
    if (part.cnc) {
      // The bounding box stays the part (picking, corners, edges); only the real outline is drawn.
      const pieces = part.cnc.outline.map((o) => {
        const g = new THREE.BoxGeometry(toMetres(o.x1 - o.x0), toMetres(o.y1 - o.y0), sz);
        g.translate(toMetres((o.x0 + o.x1) / 2) - sx / 2, toMetres((o.y0 + o.y1) / 2) - sy / 2, 0);
        return g.toNonIndexed();
      });
      const shape = new THREE.Mesh(mergeGeometries(pieces), mesh.material);
      shape.castShadow = shape.receiveShadow = !xray;
      shape.name = 'cnc-shape';
      // Invisible but still hit by the picking ray (colour/depth writes off).
      (mesh as THREE.Mesh).material = new THREE.MeshBasicMaterial({ colorWrite: false, depthWrite: false });
      mesh.add(shape);
    }
    if (part.hingeSide) mesh.add(hingeMarkers(part.hingeSide, sx, sy, sz));
    return mesh;
  }

  /** Opening preview rotates/slides a copy in the scene only — production geometry is unchanged. */
  private applyPreview(holder: THREE.Group, mesh: THREE.Mesh, part: PanelPart, mirrored: boolean, slideLength: number, open: boolean) {
    if (!open || !part.movable) return;
    if (part.movable === 'slide') {
      holder.position.z += toMetres(slideLength) * 0.7;
      return;
    }
    // Undo the evaluation-level hinge swap: the mirror transform of the group already mirrors geometry.
    const hinge = mirrored ? (part.movable === 'hinge-left' ? 'hinge-right' : 'hinge-left') : part.movable;
    const box = partBox(part);
    const pivotX = toMetres(hinge === 'hinge-left' ? box.min[0] : box.min[0] + box.size[0]);
    const pivotZ = toMetres(box.min[2] + box.size[2]);
    const angle = ((hinge === 'hinge-left' ? -1 : 1) * 100 * Math.PI) / 180;
    holder.position.set(pivotX, 0, pivotZ);
    holder.rotation.y = angle;
    holder.children.forEach((c) => c !== mesh && c.position.sub(new THREE.Vector3(pivotX, 0, pivotZ)));
    mesh.position.sub(new THREE.Vector3(pivotX, 0, pivotZ));
  }

  private addEdgeStrips(holder: THREE.Group, mesh: THREE.Mesh, part: PanelPart, dec: Record<EdgeId, string>) {
    const box = partBox(part);
    const axIdx = { x: 0, y: 1, z: 2 } as const;
    const la = axIdx[part.placement.lengthAxis];
    const wa = axIdx[part.placement.widthAxis];
    const sizes = box.size.map(toMetres);
    const t = 0.0015;
    for (const e of EDGE_IDS) {
      const d = dec[e] as keyof typeof EDGE_COLORS;
      if (d === 'none') continue;
      const s = [...sizes];
      const off = [0, 0, 0];
      const along = e.startsWith('L') ? wa : la;
      const sign = e.endsWith('1') ? -1 : 1;
      s[along] = t;
      off[along] = sign * (sizes[along]! / 2 + t / 2);
      const strip = new THREE.Mesh(new THREE.BoxGeometry(s[0]!, s[1]!, s[2]!), new THREE.MeshBasicMaterial({ color: EDGE_COLORS[d] ?? 0xffffff }));
      strip.position.copy(mesh.position).add(new THREE.Vector3(off[0], off[1], off[2]));
      holder.add(strip);
    }
  }

  setSelection(cabinetIds: string[], partId: string | null, extra: string[] = []) {
    this.selection = { cabinetIds: new Set(cabinetIds), partId, extra: new Set(extra) };
  }

  setProjection(kind: 'perspective' | 'ortho') {
    const target = this.controls.target.clone();
    const from = this.camera.position.clone();
    this.camera = kind === 'ortho' ? this.ortho : this.persp;
    this.camera.position.copy(from);
    this.controls.object = this.camera;
    this.tc.camera = this.camera;
    this.controls.target.copy(target);
    if (kind === 'ortho') {
      this.ortho.userData.size = Math.max(1, from.distanceTo(target) * 0.7);
      this.updateOrthoFrustum();
    }
    this.controls.update();
  }

  setView(view: ViewName) {
    const b = this.lastBounds.isEmpty() ? new THREE.Box3(new THREE.Vector3(0, 0, 0), new THREE.Vector3(1, 1, 1)) : this.lastBounds;
    const c = b.getCenter(new THREE.Vector3());
    const r = b.getSize(new THREE.Vector3()).length() * 1.2 + 0.5;
    const dir: Record<ViewName, THREE.Vector3> = {
      persp: new THREE.Vector3(0.55, 0.45, 0.75),
      front: new THREE.Vector3(0, 0, 1),
      back: new THREE.Vector3(0, 0, -1),
      left: new THREE.Vector3(-1, 0, 0),
      right: new THREE.Vector3(1, 0, 0),
      top: new THREE.Vector3(0, 1, 0.0001),
      bottom: new THREE.Vector3(0, -1, 0.0001),
    };
    this.controls.target.copy(c);
    this.camera.position.copy(c).add(dir[view].normalize().multiplyScalar(r));
    if (this.camera === this.ortho) {
      this.ortho.userData.size = r * 0.8;
      this.updateOrthoFrustum();
    }
    this.controls.update();
  }

  frameSelection() {
    const sel = this.meshes.filter((m) => this.selection.cabinetIds.has((m.userData as PickResult).cabinetId));
    const b = new THREE.Box3();
    (sel.length ? sel : this.meshes).forEach((m) => b.expandByObject(m));
    if (b.isEmpty()) return;
    const c = b.getCenter(new THREE.Vector3());
    const r = b.getSize(new THREE.Vector3()).length() * 1.1 + 0.3;
    const dir = this.camera.position.clone().sub(this.controls.target).normalize();
    this.controls.target.copy(c);
    this.camera.position.copy(c).add(dir.multiplyScalar(r));
    if (this.camera === this.ortho) {
      this.ortho.userData.size = r * 0.8;
      this.updateOrthoFrustum();
    }
  }

  /**
   * Glides the camera to the problem spot of an issue and marks it with a red box: the overlap of two
   * parts (collision), one part, or whole elements.
   */
  focusIssue(partIds: string[], cabinetIds: string[]) {
    this.clearIssueMarker();
    const boxOf = (pred: (u: PickResult) => boolean) => {
      const b = new THREE.Box3();
      this.meshes.filter((m) => pred(m.userData as PickResult)).forEach((m) => b.expandByObject(m));
      return b;
    };
    const parts = partIds.map((id) => boxOf((u) => u.partId === id)).filter((b) => !b.isEmpty());
    const units = cabinetIds.map((id) => boxOf((u) => u.cabinetId === id)).filter((b) => !b.isEmpty());
    const all = parts.length ? parts : units;
    if (!all.length) return;
    const view = all.reduce((acc, b) => acc.union(b), new THREE.Box3());
    // The spot itself: where the two boxes overlap, else the whole thing.
    let spot = view.clone();
    if (all.length === 2) {
      const x = all[0]!.clone().intersect(all[1]!);
      if (!x.isEmpty()) spot = x;
    }
    this.issueMarker = new THREE.Box3Helper(spot.clone().expandByScalar(0.004), new THREE.Color(0xef4444));
    (this.issueMarker.material as THREE.LineBasicMaterial).depthTest = false;
    this.issueMarker.renderOrder = 999;
    this.scene.add(this.issueMarker);
    const c = spot.getCenter(new THREE.Vector3());
    const r = Math.max(0.45, view.getSize(new THREE.Vector3()).length() * 1.1);
    const dir = this.camera.position.clone().sub(this.controls.target).normalize();
    this.glide = { t0: performance.now(), fromT: this.controls.target.clone(), toT: c, fromP: this.camera.position.clone(), toP: c.clone().add(dir.multiplyScalar(r)) };
    if (this.camera === this.ortho) {
      this.ortho.userData.size = r * 0.8;
      this.updateOrthoFrustum();
    }
  }

  clearIssueMarker() {
    if (!this.issueMarker) return;
    this.scene.remove(this.issueMarker);
    this.issueMarker.dispose();
    this.issueMarker = null;
  }

  private stepGlide() {
    const g = this.glide;
    if (!g) return;
    const t = Math.min(1, (performance.now() - g.t0) / 400);
    const e = 1 - (1 - t) ** 3;
    this.controls.target.lerpVectors(g.fromT, g.toT, e);
    this.camera.position.lerpVectors(g.fromP, g.toP, e);
    if (t >= 1) this.glide = null;
  }

  /** Square PNG thumbnail of the current view (rendered synchronously, then downscaled). */
  snapshot(size = 256): Promise<Blob | null> {
    this.renderer.render(this.scene, this.camera);
    const src = this.renderer.domElement;
    const c = document.createElement('canvas');
    c.width = size;
    c.height = size;
    const g = c.getContext('2d')!;
    const s = Math.min(src.width, src.height);
    g.drawImage(src, (src.width - s) / 2, (src.height - s) / 2, s, s, 0, 0, size, size);
    return new Promise((res) => c.toBlob(res, 'image/png'));
  }

  stats() {
    const info = this.renderer.info;
    return { meshes: this.meshes.length, calls: info.render.calls, triangles: info.render.triangles, geometries: info.memory.geometries, textures: info.memory.textures };
  }
}

/**
 * UVs in physical units: 1 UV unit = `tileMm` millimetres, so grain keeps its real scale on any panel size.
 * Texture U follows the part's grain (length) direction on every face.
 */
function setPhysicalUVs(geo: THREE.BoxGeometry, part: PanelPart, tileMm: number) {
  const uv = geo.getAttribute('uv') as THREE.BufferAttribute;
  const pos = geo.getAttribute('position') as THREE.BufferAttribute;
  const box = partBox(part);
  const sizeMm = box.size.map((v) => v / 1000);
  const axIdx = { x: 0, y: 1, z: 2 } as const;
  const grainAxis = part.grain === 'width' ? axIdx[part.placement.widthAxis] : axIdx[part.placement.lengthAxis];
  // BoxGeometry face order: +x, -x, +y, -y, +z, -z; each face has 4 vertices.
  for (let face = 0; face < 6; face++) {
    const normalAxis = Math.floor(face / 2);
    const others = [0, 1, 2].filter((a) => a !== normalAxis) as [number, number];
    // U = grain axis if it lies in this face, otherwise the first in-plane axis.
    const uAxis = others.includes(grainAxis) ? grainAxis : others[0];
    const vAxis = others.find((a) => a !== uAxis)!;
    for (let k = 0; k < 4; k++) {
      const i = face * 4 + k;
      const p = [pos.getX(i), pos.getY(i), pos.getZ(i)];
      const u = ((p[uAxis]! * 1000 + sizeMm[uAxis]! / 2) / tileMm);
      const v = ((p[vAxis]! * 1000 + sizeMm[vAxis]! / 2) / tileMm);
      uv.setXY(i, u, v);
    }
  }
  uv.needsUpdate = true;
}
